import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Link,
  CircularProgress,
  Divider,
  Button,
  IconButton,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Headset as HeadsetIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  LocationOn as LocationOnIcon,
  WhatsApp as WhatsAppIcon,
  Facebook as FacebookIcon,
} from '@mui/icons-material';
import { ConfigLinksDetailsContext } from '../Context/cofingLinksDetailsContext';
import { isMobile } from 'react-device-detect';

const ContactUs = () => {
  const { t, i18n } = useTranslation();
  const { configLinks } = useContext(ConfigLinksDetailsContext);
  const isRTL = i18n.language === 'ar';

  const [workingHours, setWorkingHours] = useState([]);

  useEffect(() => {
    // Parse working hours from API
    const hoursText = isRTL ? configLinks.workingHoursAr : configLinks.workingHoursEn;
    if (hoursText) {
      const lines = hoursText.split('\n').filter(line => line.trim());
      setWorkingHours(lines);
    }
  }, [configLinks, isRTL]);

  const contactInfo = [
    {
      icon: HeadsetIcon,
      label: t('publicServicesPhone'),
      value: configLinks.publicServicesPhone,
      action: configLinks.publicServicesPhone ? `tel:${configLinks.publicServicesPhone}` : null,
      show: !!configLinks.publicServicesPhone,
    },
    {
      icon: PhoneIcon,
      label: t('phone'),
      value: configLinks.phone,
      action: configLinks.phone ? `tel:${configLinks.phone}` : null,
      show: !!configLinks.phone,
    },
    {
      icon: PrintIcon,
      label: t('fax'),
      value: configLinks.fax,
      action: null,
      show: !!configLinks.fax,
    },
    {
      icon: EmailIcon,
      label: t('email'),
      value: configLinks.email,
      action: configLinks.email ? `mailto:${configLinks.email}` : null,
      show: !!configLinks.email,
    },
    {
      icon: LanguageIcon,
      label: t('website'),
      value: configLinks.webSite,
      action: configLinks.webSite
        ? configLinks.webSite.startsWith('http')
          ? configLinks.webSite
          : `https://${configLinks.webSite}`
        : null,
      show: !!configLinks.webSite,
    },
    {
      icon: LocationOnIcon,
      label: t('address'),
      value: configLinks.address,
      action: null,
      show: !!configLinks.address,
    },
  ].filter(item => item.show);

  const socialLinks = [
    {
      name: 'WhatsApp',
      icon: WhatsAppIcon,
      url: configLinks.whatsapp,
      color: '#25D366',
      show: !!configLinks.whatsapp,
    },
    {
      name: 'Facebook',
      icon: FacebookIcon,
      url: configLinks.facebook,
      color: '#1877F2',
      show: !!configLinks.facebook,
    },
  ].filter(item => item.show);

  return (
    <Box
      sx={{
        py: isMobile ? 2 : 4,
        backgroundColor: '#f5f5f5',
        minHeight: '80vh',
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 4,
            background: 'linear-gradient(135deg, #BC1F41 0%, #8B1538 100%)',
            borderRadius: 2,
            p: isMobile ? 2 : 4,
            color: 'white',
          }}
        >
          <HeadsetIcon sx={{ fontSize: 50, mb: 1 }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('contactUs')}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {t('weAreHereToHelp')}
          </Typography>
        </Box>

        {/* Working Hours */}
        {workingHours.length > 0 && (
          <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#BC1F41',
                  textAlign: isRTL ? 'right' : 'left',
                }}
              >
                {t('workingHours')}
              </Typography>
              <Grid container spacing={1}>
                {workingHours.map((hour, index) => {
                  const parts = hour.split(':');
                  return (
                    <Grid item xs={12} key={index}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          p: 1,
                          backgroundColor: index % 2 === 0 ? '#fafafa' : 'transparent',
                          borderRadius: 1,
                          borderBottom: index < workingHours.length - 1 ? '1px solid #e0e0e0' : 'none',
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          {parts[0].trim()}
                        </Typography>
                        <Typography sx={{ color: '#666' }}>
                          {parts.slice(1).join(':').trim()}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Contact Information */}
        {contactInfo.length > 0 && (
          <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#BC1F41',
                  textAlign: isRTL ? 'right' : 'left',
                }}
              >
                {t('contactInformation')}
              </Typography>
              <Box>
                {contactInfo.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <Box key={index}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          p: 2,
                          backgroundColor: index % 2 === 0 ? '#fafafa' : 'transparent',
                          borderRadius: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '50%',
                            backgroundColor: '#BC1F4115',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <IconComponent sx={{ color: '#BC1F41', fontSize: 28 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              color: '#666',
                              textAlign: isRTL ? 'right' : 'left',
                            }}
                          >
                            {item.label}
                          </Typography>
                          {item.action ? (
                            <Link
                              href={item.action}
                              target={item.action.startsWith('http') ? '_blank' : '_self'}
                              sx={{
                                fontSize: '1rem',
                                fontWeight: 600,
                                color: '#BC1F41',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                                textAlign: isRTL ? 'right' : 'left',
                                display: 'block',
                              }}
                            >
                              {item.value}
                            </Link>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: '1rem',
                                fontWeight: 600,
                                color: '#333',
                                textAlign: isRTL ? 'right' : 'left',
                              }}
                            >
                              {item.value}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {index < contactInfo.length - 1 && (
                        <Divider sx={{ my: 0 }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Social Media */}
        {socialLinks.length > 0 && (
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#BC1F41',
                  textAlign: 'center',
                }}
              >
                {t('followUs')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  return (
                    <IconButton
                      key={index}
                      component="a"
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: social.color,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: social.color,
                          opacity: 0.8,
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <IconComponent sx={{ fontSize: 32 }} />
                    </IconButton>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {contactInfo.length === 0 && socialLinks.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={50} />
            <Typography sx={{ mt: 2, color: '#666' }}>
              {t('loading')}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ContactUs;
